import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * Skeleton shell shown during the initial desk fetch.
 * Matches the real layout silhouette so there's no layout shift when data arrives.
 */
export function DeskLoadingSkeleton() {
  return (
    <div className="h-full flex flex-col">
      {/* Top bar skeleton */}
      <div
        className="flex items-center gap-3 px-4 py-2.5 border-b"
        style={{
          borderColor: "var(--blt)",
          background: "var(--surface)",
        }}
      >
        <div
          className="w-6 h-6 rounded animate-pulse"
          style={{ background: "var(--border)" }}
        />
        <div
          className="w-2 h-2 rounded-full animate-pulse"
          style={{ background: "var(--border)" }}
        />
        <div
          className="h-4 rounded animate-pulse"
          style={{ width: "200px", background: "var(--border)" }}
        />
        <div
          className="h-4 w-14 rounded animate-pulse"
          style={{ background: "var(--border)", opacity: 0.5 }}
        />
      </div>

      {/* Main area: panels silhouette */}
      <div className="flex-1 flex overflow-hidden">
        {/* Files sidebar silhouette */}
        <div
          className="hidden md:block w-[220px] border-r"
          style={{ borderColor: "var(--blt)" }}
        >
          <div className="p-3 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-4 rounded animate-pulse"
                style={{
                  background: "var(--border)",
                  opacity: 1 - i * 0.08,
                  width: `${60 + (i % 3) * 15}%`,
                }}
              />
            ))}
          </div>
        </div>
        {/* Session area silhouette */}
        <div className="flex-1 p-4">
          <div
            className="h-full rounded-lg border animate-pulse"
            style={{ borderColor: "var(--blt)", background: "var(--surface)" }}
          />
        </div>
      </div>

      {/* Bottom bar skeleton */}
      <div
        className="hidden md:flex items-center gap-4 px-4 py-1.5 border-t"
        style={{ borderColor: "var(--blt)", background: "var(--surface)" }}
      >
        <div
          className="h-3 rounded animate-pulse"
          style={{ width: "220px", background: "var(--border)", opacity: 0.5 }}
        />
      </div>
    </div>
  );
}

/**
 * Shown when the desk is in "creating" state — new sprite being deployed.
 * Can take 10-30s. Shows an animated icon, sprite name, and elapsed time
 * so the user knows it's making progress.
 */
export function DeployingScreen({
  spriteName,
  createdAt,
}: {
  spriteName: string | null;
  createdAt: string;
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = new Date(
      createdAt + (createdAt.includes("Z") ? "" : "Z"),
    ).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [createdAt]);

  return (
    <div className="h-full flex flex-col">
      <div
        className="flex items-center gap-3 px-4 py-2.5 border-b"
        style={{ borderColor: "var(--blt)", background: "var(--surface)" }}
      >
        <Link
          to="/"
          className="p-1 -ml-1 rounded hover:bg-[var(--hover)]"
          style={{ color: "var(--t3)" }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
        <img
          src="/frogs/waking.gif"
          alt=""
          className="w-16 h-16"
          style={{ opacity: 0.85 }}
        />
        <div className="text-center space-y-1">
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-xl)",
              fontWeight: 600,
              color: "var(--t1)",
              letterSpacing: "-0.4px",
            }}
          >
            Deploying your desk
          </h2>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--t3)" }}>
            Setting up the sprite. Usually takes 10–20 seconds.
          </p>
        </div>
        <div
          className="flex items-center gap-3 text-[11px] pt-2"
          style={{ fontFamily: "var(--font-mono)", color: "var(--t4)" }}
        >
          {spriteName && <span>{spriteName}</span>}
          <span className="tabular-nums">{elapsed}s</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Shown when the desk has entered a terminal error state, or we can't load it.
 */
export function DeskErrorScreen({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="h-full flex flex-col">
      <div
        className="flex items-center gap-3 px-4 py-2.5 border-b"
        style={{ borderColor: "var(--blt)", background: "var(--surface)" }}
      >
        <Link
          to="/"
          className="p-1 -ml-1 rounded hover:bg-[var(--hover)]"
          style={{ color: "var(--t3)" }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 max-w-md mx-auto text-center">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--err)"
          strokeWidth="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <circle cx="12" cy="16" r="0.5" fill="currentColor" />
        </svg>
        <div className="space-y-1">
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-lg)",
              fontWeight: 600,
              color: "var(--t1)",
            }}
          >
            {title}
          </h2>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--t3)" }}>
            {message}
          </p>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Link
            to="/"
            className="text-[13px] px-3 py-1.5 rounded border"
            style={{
              borderColor: "var(--border)",
              color: "var(--t3)",
            }}
          >
            Back to desks
          </Link>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-[13px] px-3 py-1.5 rounded"
              style={{
                background: "var(--accent)",
                color: "var(--white)",
              }}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
